import * as Lucide from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Symboler i `src/assets/icons/sr-icons.svg`. Må holdes i sync med
 * `<symbol id="sr-*">`-definisjonene der.
 */
const SPRITE_NAMES = new Set<string>([
  "alert",
  "arrow-right",
  "asterisk",
  "award",
  "bell",
  "breathing-blob",
  "briefcase",
  "calendar",
  "check",
  "check-circle",
  "chevron-left",
  "chevron-right",
  "clipboard-check",
  "clipboard-list",
  "clock",
  "code2",
  "dashboard",
  "database",
  "download",
  "edit",
  "empty-blob",
  "external-link",
  "eye",
  "eye-off",
  "file",
  "film",
  "filter",
  "flag",
  "folder",
  "fox",
  "info",
  "loader2",
  "logout",
  "mail",
  "megaphone",
  "menu",
  "message-square",
  "money",
  "monitor",
  "more",
  "paperclip",
  "pause",
  "play",
  "plus",
  "printer",
  "receipt",
  "refresh-cw",
  "rotate-ccw",
  "save",
  "scribble-circle",
  "scribble-underline",
  "scroll-text",
  "search",
  "send",
  "settings",
  "sparkles",
  "star",
  "sticky-note",
  "target",
  "trash",
  "trending-down",
  "trending-up",
  "trophy",
  "undo-2",
  "upload",
  "user",
  "users",
  "x",
  "x-circle",
]);

/**
 * Aliaser fra vanlige Lucide-varianter til eksisterende sprite-ikoner.
 * Lar oss bruke `<SrIcon name="trash2" />` osv. uten å duplisere SVG.
 */
const ALIASES: Record<string, string> = {
  trash2: "trash",
  edit2: "edit",
  pencil: "edit",
  "file-text": "file",
  "file-warning": "alert",
  "alert-circle": "alert",
  "alert-triangle": "alert",
  users2: "users",
  settings2: "settings",
  "layout-dashboard": "dashboard",
  "folder-kanban": "folder",
  "folder-open": "folder",
  "more-horizontal": "more",
  "more-vertical": "more",
  "check-circle2": "check-circle",
  "check-circle-2": "check-circle",
  "circle-check": "check-circle",
  "circle-x": "x-circle",
  cross: "x",
  banknote: "money",
  coins: "money",
  wallet: "money",
  tv: "monitor",
  display: "monitor",
};

/**
 * Roterte varianter — sprite har f.eks. arrow-right, så arrow-left/up/down
 * rendres ved å rotere det samme symbolet.
 */
const ROTATIONS: Record<string, { base: string; deg: number }> = {
  "arrow-left": { base: "arrow-right", deg: 180 },
  "arrow-up": { base: "arrow-right", deg: -90 },
  "arrow-down": { base: "arrow-right", deg: 90 },
  "chevron-up": { base: "chevron-right", deg: -90 },
  "chevron-down": { base: "chevron-right", deg: 90 },
  "chevrons-up-down": { base: "more", deg: 0 }, // best-effort
};

export type SrIconName = string;

function toPascalCase(input: string): string {
  return input
    .split(/[-_\s]/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
}

interface SrIconProps extends Omit<React.SVGAttributes<SVGSVGElement>, "name"> {
  name: SrIconName;
  size?: number | string;
  strokeWidth?: number;
  /** Native tooltip. Videresendes til underliggende svg/Lucide via rest-spread. */
  title?: string;
}

/**
 * Unified icon component. Prefers Sølvrev sprite; faller tilbake til Lucide
 * ved ukjente navn. Skrives med kebab-case (f.eks. `name="arrow-right"`).
 */
export function SrIcon({ name, size = 16, className, strokeWidth, style, ...rest }: SrIconProps) {
  // Direct sprite hit
  if (SPRITE_NAMES.has(name)) {
    return (
      <svg
        width={size}
        height={size}
        aria-hidden="true"
        className={cn("shrink-0", className)}
        style={style}
        {...rest}
      >
        <use href={`#sr-${name}`} />
      </svg>
    );
  }

  // Alias to sprite
  const alias = ALIASES[name];
  if (alias) {
    return (
      <svg
        width={size}
        height={size}
        aria-hidden="true"
        className={cn("shrink-0", className)}
        style={style}
        {...rest}
      >
        <use href={`#sr-${alias}`} />
      </svg>
    );
  }

  // Rotated variant
  const rot = ROTATIONS[name];
  if (rot) {
    return (
      <svg
        width={size}
        height={size}
        aria-hidden="true"
        className={cn("shrink-0", className)}
        style={{ ...style, transform: `rotate(${rot.deg}deg)` }}
        {...rest}
      >
        <use href={`#sr-${rot.base}`} />
      </svg>
    );
  }

  // Lucide fallback
  const pascal = toPascalCase(name);
  const LucideIcon = (Lucide as unknown as Record<string, React.ComponentType<Lucide.LucideProps>>)[pascal];
  if (LucideIcon) {
    return (
      <LucideIcon
        size={size}
        strokeWidth={strokeWidth}
        className={cn("shrink-0", className)}
        style={style}
        {...(rest as Lucide.LucideProps)}
      />
    );
  }

  if (import.meta.env.DEV) {
    console.warn(`[SrIcon] Unknown icon name: "${name}". Not in sprite, not aliased, not in lucide-react.`);
  }
  return null;
}

export default SrIcon;
